import { CustomFieldsView, useCustomColumns } from '../components/CustomFields.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  AllergyNote,
  CustomerForm,
  initials,
  phoneText,
  useCustomerSearch,
  VisitStats,
  type CustomerRow,
} from '../components/Customers.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { CustomerLoyalty, type ProfileLoyalty } from '../components/Loyalty.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action, ApiError } from '../lib/api.ts';
import { useLiveAction } from '../lib/data.ts';
import { dateTime, day, localDay, money, qty, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useNow, useUi } from '../lib/ui-state.tsx';
import { useLinkParam } from '../lib/link-params.ts';
import { RecordNotes } from '../components/RecordNotes.tsx';

type State = 'booked' | 'seated' | 'done' | 'cancelled' | 'no_show';
type Source = 'phone' | 'walk_in' | 'online' | 'other';
interface Booking {
  id: string;
  number: number;
  customer: string;
  customer_name: string;
  phone: string | null;
  allergies: string | null;
  vip: boolean;
  customer_note: string | null;
  visits: number;
  no_shows: number;
  guests: number;
  starts_at: string;
  ends_at: string;
  time: string;
  until: string;
  table: string | null;
  table_name: string | null;
  state: State;
  source: Source;
  note: string | null;
  order: string | null;
  cancel_reason: string | null;
}
interface DayView {
  date: string;
  today: string;
  reservations: Booking[];
  tables: { id: string; name: string; seats: number; floor: string }[];
  summary: { bookings: number; guests: number; seated: number; waiting: number; no_shows: number };
}
interface FreeTable {
  id: string;
  name: string;
  seats: number;
  floor: string;
  fits: boolean;
}

const STATE_BADGE: Record<State, { tone: Tone; icon: IconName; label: MessageKey }> = {
  booked: { tone: 'info', icon: 'clock', label: 'res.state_booked' },
  seated: { tone: 'primary', icon: 'table', label: 'res.state_seated' },
  done: { tone: 'ok', icon: 'check', label: 'res.state_done' },
  cancelled: { tone: 'neutral', icon: 'x', label: 'res.state_cancelled' },
  no_show: { tone: 'danger', icon: 'alert', label: 'res.state_no_show' },
};
const SOURCES: Source[] = ['phone', 'walk_in', 'online', 'other'];
const DURATIONS = [60, 90, 120, 180];

const shiftDay = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const weekday = (d: string) => new Intl.DateTimeFormat(locale(), { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
/** Times every 15 minutes, as offered for a booking. */
const SLOTS = Array.from({ length: 4 * 24 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`).filter(
  (s) => s >= '07:00',
);
/** The next slot from now, for a new booking today. */
function nextSlot(): string {
  const d = new Date(Date.now() + 30 * 60_000);
  const m = Math.ceil(d.getMinutes() / 15) * 15;
  const h = d.getHours() + Math.floor(m / 60);
  if (h >= 24) return '20:00';
  return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Front of house: today's bookings and the customers the business knows. */
export function Guests() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'customers' ? 'customers' : 'bookings';
  const { can } = useSession();
  return (
    <Shell>
      <div className="page guests">
        <div className="page-head">
          <div>
            <h1>{t('guests.title')}</h1>
            <p className="muted">{t('guests.subtitle')}</p>
          </div>
        </div>
        {can('reservations.view') ? (
          <div className="segmented tabs" role="tablist" aria-label={t('guests.title')}>
            {(['bookings', 'customers'] as const).map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setParams(k === 'bookings' ? {} : { tab: k }, { replace: true })}>
                <Icon name={k === 'bookings' ? 'calendar' : 'users'} size={16} />
                {t(`guests.tab_${k}`)}
              </button>
            ))}
          </div>
        ) : null}
        {tab === 'bookings' && can('reservations.view') ? <Bookings /> : <Customers />}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------- Bookings */

function Bookings() {
  const navigate = useNavigate();
  const { can } = useSession();
  const { fail, toast } = useUi();
  const now = useNow();
  const [params, setParams] = useSearchParams();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') ?? '') ? params.get('date')! : todayIso();
  const setDate = (d: string) => {
    const p = new URLSearchParams(params);
    if (d === todayIso()) p.delete('date');
    else p.set('date', d);
    setParams(p, { replace: true });
  };
  const { data, reload } = useLiveAction<DayView>('reservations.day', { date }, ['reservation', 'restaurant']);
  const [editing, setEditing] = useState<Booking | 'new' | null>(null);
  // Quick create: /guests?new=booking.
  useLinkParam('new', (kind) => kind === 'booking' && manage && setEditing('new'));
  const [seating, setSeating] = useState<(Booking & { busy?: boolean }) | null>(null);
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [show, setShow] = useState<'active' | 'all'>('active');
  const manage = can('reservations.manage');
  const isToday = data ? data.date === data.today : date === todayIso();

  const rows = useMemo(() => (data?.reservations ?? []).filter((r) => show === 'all' || r.state === 'booked' || r.state === 'seated'), [data, show]);
  const hidden = (data?.reservations.length ?? 0) - rows.length;

  const seat = async (b: Booking) => {
    if (!b.table) {
      setSeating(b);
      return;
    }
    try {
      const r = await action<{ order: string }>('reservations.seat', { reservation: b.id });
      toast(t('res.seated', { name: b.customer_name, table: b.table_name ?? '' }), 'ok');
      navigate(`/pos/order/${r.order}`);
    } catch (err) {
      // The booked table is still in use: offer the free ones instead.
      if (err instanceof ApiError && err.code === 'conflict' && err.details?.order) setSeating({ ...b, busy: true });
      else fail(err);
    }
  };
  const noShow = async (b: Booking) => {
    try {
      await action('reservations.no_show', { reservation: b.id });
      toast(t('res.marked_no_show', { name: b.customer_name }), 'info');
      await reload();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-group day-nav">
          <IconButton icon="back" label={t('res.prev_day')} onClick={() => setDate(shiftDay(date, -1))} />
          <div className="day-nav-label">
            <span className="strong">{isToday ? t('res.today') : weekday(date)}</span>
            <span className="muted">{day(date)}</span>
          </div>
          <IconButton icon="chevronRight" label={t('res.next_day')} onClick={() => setDate(shiftDay(date, 1))} />
          <input className="input date-input" type="date" aria-label={t('res.pick_day')} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          {!isToday ? (
            <Button variant="ghost" size="sm" onClick={() => setDate(todayIso())}>
              {t('res.back_today')}
            </Button>
          ) : null}
        </div>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            {t('res.new')}
          </Button>
        ) : null}
      </div>

      {!data ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <>
          <dl className="fiscal-stats">
            <div className="fiscal-stat">
              <dt>{t('res.k_bookings')}</dt>
              <dd>{data.summary.bookings}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('res.k_guests')}</dt>
              <dd>{data.summary.guests}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('res.k_waiting')}</dt>
              <dd>{data.summary.waiting}</dd>
            </div>
            <div className={`fiscal-stat${data.summary.no_shows ? ' danger' : ''}`}>
              <dt>{t('res.k_no_shows')}</dt>
              <dd>{data.summary.no_shows}</dd>
            </div>
          </dl>

          <section className="list-card" aria-label={t('guests.tab_bookings')}>
            <div className="list-card-head">
              <h2>{isToday ? t('res.list_today') : t('res.list_day', { day: day(date) })}</h2>
              <div className="segmented subtle" role="radiogroup" aria-label={t('res.show')}>
                {(['active', 'all'] as const).map((k) => (
                  <button key={k} role="radio" aria-checked={show === k} className={show === k ? 'active' : ''} onClick={() => setShow(k)}>
                    {t(`res.show_${k}`)}
                  </button>
                ))}
              </div>
            </div>
            {rows.length === 0 ? (
              <EmptyState
                icon="calendar"
                title={hidden ? t('res.none_open') : t('res.none')}
                body={hidden ? tn('res.hidden', hidden) : manage ? t('res.none_body') : undefined}
              />
            ) : (
              rows.map((b) => {
                const late = b.state === 'booked' && isToday && new Date(b.starts_at).getTime() < now;
                const lateMin = Math.floor((now - new Date(b.starts_at).getTime()) / 60_000);
                const badge = STATE_BADGE[b.state];
                return (
                  <div key={b.id} className={`booking-row state-${b.state}`}>
                    <button className="booking-main" onClick={() => (b.state === 'booked' && manage ? setEditing(b) : b.order ? navigate(`/pos/order/${b.order}`) : setEditing(b))}>
                      <span className="booking-time">
                        <span className="strong">{b.time}</span>
                        <span className="muted">{b.until}</span>
                      </span>
                      <span className="booking-who">
                        <span className="strong">
                          {b.customer_name} {b.vip ? <span className="vip">{t('cust.vip')}</span> : null}
                        </span>
                        <span className="booking-meta">
                          <span>
                            <Icon name="users" size={14} /> {b.guests}
                          </span>
                          <span>
                            <Icon name="table" size={14} /> {b.table_name ? t('table.name', { name: b.table_name }) : t('res.no_table')}
                          </span>
                          {b.phone ? <span>{phoneText(b.phone)}</span> : null}
                          {b.visits ? <span>{tn('cust.visits', b.visits)}</span> : <span>{t('cust.first_visit')}</span>}
                          {b.no_shows ? <span className="text-danger">{tn('res.past_no_shows', b.no_shows)}</span> : null}
                        </span>
                        {b.allergies ? <AllergyNote text={b.allergies} compact /> : null}
                        {b.note ? <span className="booking-note">{b.note}</span> : null}
                        {b.state === 'cancelled' && b.cancel_reason ? <span className="muted">{b.cancel_reason}</span> : null}
                      </span>
                    </button>
                    <span className="booking-end">
                      {late ? <StatusBadge tone="warn" icon="clock" label={t('res.late', { n: lateMin })} /> : <StatusBadge tone={badge.tone} icon={badge.icon} label={t(badge.label)} />}
                      {manage && b.state === 'booked' ? (
                        <span className="booking-actions">
                          {isToday ? (
                            <Button size="sm" variant="primary" icon="check" onClick={() => void seat(b)}>
                              {t('res.seat')}
                            </Button>
                          ) : null}
                          {late ? (
                            <Button size="sm" variant="ghost" onClick={() => void noShow(b)}>
                              {t('res.no_show')}
                            </Button>
                          ) : null}
                          <IconButton icon="x" label={t('res.cancel')} onClick={() => setCancelling(b)} />
                        </span>
                      ) : b.state === 'seated' && b.order ? (
                        <Button size="sm" variant="secondary" icon="receipt" onClick={() => navigate(`/pos/order/${b.order}`)}>
                          {t('res.open_bill')}
                        </Button>
                      ) : null}
                    </span>
                  </div>
                );
              })
            )}
          </section>
        </>
      )}
      {editing ? (
        <BookingDialog
          booking={editing === 'new' ? null : editing}
          date={date}
          readOnly={editing !== 'new' && (editing.state !== 'booked' || !manage)}
          onClose={() => setEditing(null)}
          onSaved={async (savedDay) => {
            setEditing(null);
            if (savedDay !== date) setDate(savedDay);
            await reload();
          }}
        />
      ) : null}
      {seating ? <SeatDialog booking={seating} onClose={() => setSeating(null)} /> : null}
      {cancelling ? <CancelBooking booking={cancelling} onClose={() => setCancelling(null)} onDone={reload} /> : null}
    </>
  );
}

function BookingDialog({
  booking,
  date: initialDate,
  readOnly,
  onClose,
  onSaved,
}: {
  booking: Booking | null;
  date: string;
  readOnly: boolean;
  onClose: () => void;
  onSaved: (day: string) => Promise<void>;
}) {
  const { fail, toast } = useUi();
  const [customer, setCustomer] = useState<{ id: string; name: string; phone: string | null; allergies: string | null; visits: number } | null>(
    booking ? { id: booking.customer, name: booking.customer_name, phone: booking.phone, allergies: booking.allergies, visits: booking.visits } : null,
  );
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [guests, setGuests] = useState(booking?.guests ?? 2);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(booking?.time ?? (initialDate === todayIso() ? nextSlot() : '20:00'));
  const [minutes, setMinutes] = useState(booking ? Math.round((Date.parse(booking.ends_at) - Date.parse(booking.starts_at)) / 60_000) : 120);
  const [table, setTable] = useState<string>(booking?.table ?? '');
  const [source, setSource] = useState<Source>(booking?.source ?? 'phone');
  const [note, setNote] = useState(booking?.note ?? '');
  const [free, setFree] = useState<FreeTable[] | null>(null);
  const [busy, setBusy] = useState(false);
  const search = (name || phone).trim();
  const matches = useCustomerSearch(customer || search.length < 2 ? '' : search);
  const suggestions = customer || search.length < 2 ? [] : (matches ?? []).slice(0, 4);

  useEffect(() => {
    if (readOnly) return;
    setFree(null);
    const timer = setTimeout(
      () => void action<FreeTable[]>('reservations.free_tables', { date, time, minutes, guests, except: booking?.id }).then(setFree, fail),
      150,
    );
    return () => clearTimeout(timer);
  }, [date, time, minutes, guests, booking?.id, readOnly, fail]);

  const tableGone = !!table && !!free && !free.some((f) => f.id === table);
  const save = async () => {
    setBusy(true);
    try {
      await action('reservations.save', {
        reservation: booking?.id,
        ...(customer ? { customer: customer.id } : { name: name.trim(), phone: phone.trim() || null }),
        guests,
        date,
        time,
        minutes,
        table: table || null,
        source,
        note: note.trim() || null,
      });
      toast(booking ? t('res.updated') : t('res.booked', { name: customer?.name ?? name.trim(), time, day: day(date) }), 'ok');
      await onSaved(date);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  const quick = [1, 2, 3, 4, 5, 6, 8, 10];

  return (
    <Dialog
      title={booking ? t('res.booking_n', { n: booking.number }) : t('res.new')}
      onClose={onClose}
      size="lg"
      footer={
        readOnly ? (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={(!customer && name.trim().length < 2) || tableGone} onClick={save}>
              {booking ? t('common.save') : t('res.book')}
            </Button>
          </>
        )
      }
    >
      {readOnly && booking ? (
        <div className="report-wrap">
          <StatusBadge tone={STATE_BADGE[booking.state].tone} icon={STATE_BADGE[booking.state].icon} label={t(STATE_BADGE[booking.state].label)} />
          <dl className="report">
            <dt>{t('res.guest')}</dt>
            <dd>{booking.customer_name}</dd>
            <dt>{t('res.when')}</dt>
            <dd>
              {day(initialDate)}, {booking.time}–{booking.until}
            </dd>
            <dt>{t('res.party')}</dt>
            <dd>{tn('cust.people', booking.guests)}</dd>
            <dt>{t('res.table')}</dt>
            <dd>{booking.table_name ? t('table.name', { name: booking.table_name }) : t('res.no_table')}</dd>
            <dt>{t('res.source')}</dt>
            <dd>{t(`res.src_${booking.source}`)}</dd>
          </dl>
          {booking.note ? <p className="booking-note">{booking.note}</p> : null}
          {booking.cancel_reason ? <p className="muted">{t('res.cancel_reason', { reason: booking.cancel_reason })}</p> : null}
        </div>
      ) : (
        <div className="booking-form">
          <fieldset className="fieldset">
            <legend>{t('res.guest')}</legend>
            {customer ? (
              <div className="chosen-customer">
                <span className="avatar" aria-hidden="true">
                  {initials(customer.name)}
                </span>
                <span className="pick-main">
                  <span className="strong">{customer.name}</span>
                  <span className="muted">{[phoneText(customer.phone), customer.visits ? tn('cust.visits', customer.visits) : t('cust.first_visit')].filter(Boolean).join(' · ')}</span>
                  {customer.allergies ? <AllergyNote text={customer.allergies} compact /> : null}
                </span>
                {!booking ? (
                  <Button variant="ghost" size="sm" onClick={() => setCustomer(null)}>
                    {t('cust.change')}
                  </Button>
                ) : null}
              </div>
            ) : (
              <>
                <div className="form-grid">
                  <Field label={t('cust.name')}>
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" data-autofocus />
                  </Field>
                  <Field label={t('cust.phone')} hint={source === 'walk_in' ? t('res.phone_optional') : undefined}>
                    <input className="input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
                  </Field>
                </div>
                {suggestions.length ? (
                  <div className="suggest" aria-label={t('res.known_guests')}>
                    <span className="muted">{t('res.known_guests')}</span>
                    {suggestions.map((c: CustomerRow) => (
                      <button key={c.id} className="pick-row compact" onClick={() => setCustomer(c)}>
                        <span className="avatar sm" aria-hidden="true">
                          {initials(c.name)}
                        </span>
                        <span className="pick-main">
                          <span className="strong">{c.name}</span>
                          <span className="muted">{[phoneText(c.phone), tn('cust.visits', c.visits)].filter(Boolean).join(' · ')}</span>
                        </span>
                        <span className="muted">{t('res.use')}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </fieldset>

          <fieldset className="fieldset">
            <legend>{t('res.party')}</legend>
            <div className="guest-grid" role="radiogroup" aria-label={t('res.party')}>
              {quick.map((n) => (
                <button key={n} role="radio" aria-checked={guests === n} className={`guest-btn${guests === n ? ' active' : ''}`} onClick={() => setGuests(n)}>
                  {n}
                </button>
              ))}
              <input
                className="input guest-more"
                type="number"
                min={1}
                max={200}
                aria-label={t('res.other_party')}
                placeholder={t('res.other')}
                value={quick.includes(guests) ? '' : guests}
                onChange={(e) => Number(e.target.value) > 0 && setGuests(Math.min(200, Number(e.target.value)))}
              />
            </div>
          </fieldset>

          <div className="form-grid three">
            <Field label={t('res.date')}>
              <input className="input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            </Field>
            <Field label={t('res.time')}>
              <select className="input" value={time} onChange={(e) => setTime(e.target.value)}>
                {(SLOTS.includes(time) ? SLOTS : [time, ...SLOTS]).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label={t('res.length')}>
              <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {(DURATIONS.includes(minutes) ? DURATIONS : [...DURATIONS, minutes]).map((m) => (
                  <option key={m} value={m}>
                    {m % 60 ? t('res.hours_half', { h: Math.floor(m / 60) }) : tn('res.hours', m / 60)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t('res.table')} error={tableGone ? t('res.table_taken') : null} hint={!tableGone ? t('res.table_hint') : undefined}>
            <select className="input" value={table} onChange={(e) => setTable(e.target.value)} disabled={!free}>
              <option value="">{t('res.no_table_yet')}</option>
              {tableGone ? <option value={table}>{t('res.taken_option')}</option> : null}
              {(free ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {t('res.table_option', { name: f.name, seats: f.seats, floor: f.floor })}
                  {f.fits ? '' : ` · ${t('res.too_small')}`}
                </option>
              ))}
            </select>
          </Field>

          <fieldset className="fieldset">
            <legend>{t('res.source')}</legend>
            <div className="segmented subtle" role="radiogroup" aria-label={t('res.source')}>
              {SOURCES.map((s) => (
                <button key={s} role="radio" aria-checked={source === s} className={source === s ? 'active' : ''} onClick={() => setSource(s)}>
                  {t(`res.src_${s}`)}
                </button>
              ))}
            </div>
          </fieldset>
          <Field label={t('res.note')}>
            <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('res.note_example')} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

/** Seat a booking that has no table yet: pick one that is free now. */
function SeatDialog({ booking, onClose }: { booking: Booking & { busy?: boolean }; onClose: () => void }) {
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const [free, setFree] = useState<FreeTable[] | null>(null);
  const [table, setTable] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const d = new Date();
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    void action<FreeTable[]>('reservations.free_tables', { date: todayIso(), time, minutes: 90, guests: booking.guests, except: booking.id, now: true }).then((rows) => {
      setFree(rows);
      setTable(rows[0]?.id ?? '');
    }, fail);
  }, [booking.guests, fail]);
  const seat = async () => {
    setBusy(true);
    try {
      const r = await action<{ order: string }>('reservations.seat', { reservation: booking.id, table });
      toast(t('res.seated', { name: booking.customer_name, table: free?.find((f) => f.id === table)?.name ?? '' }), 'ok');
      navigate(`/pos/order/${r.order}`);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('res.seat_title', { name: booking.customer_name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!table} onClick={seat}>
            {t('res.seat')}
          </Button>
        </>
      }
    >
      {booking.busy ? (
        <p className="booking-callout warn">
          <Icon name="alert" size={18} />
          <span>{t('res.table_busy', { table: booking.table_name ?? '' })}</span>
        </p>
      ) : null}
      <p className="muted">{t('res.seat_hint', { n: booking.guests })}</p>
      {!free ? (
        <Skeleton h={120} />
      ) : free.length === 0 ? (
        <EmptyState icon="table" title={t('res.no_free')} />
      ) : (
        <div className="table-pick" role="radiogroup" aria-label={t('res.table')}>
          {free.map((f) => (
            <button key={f.id} role="radio" aria-checked={table === f.id} className={`table-pick-btn${table === f.id ? ' active' : ''}${f.fits ? '' : ' small'}`} onClick={() => setTable(f.id)}>
              <span className="strong">{f.name}</span>
              <span className="muted">{tn('table.seats', f.seats)}</span>
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
}

function CancelBooking({ booking, onClose, onDone }: { booking: Booking; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useUi();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const chips = [t('res.reason_called'), t('res.reason_plans'), t('res.reason_twice')];
  const go = async () => {
    setBusy(true);
    try {
      await action('reservations.cancel', { reservation: booking.id, reason });
      toast(t('res.cancelled', { name: booking.customer_name }), 'info');
      onClose();
      await onDone();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('res.cancel_title', { name: booking.customer_name, time: booking.time })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('res.keep')}
          </Button>
          <Button variant="danger" icon="x" busy={busy} disabled={reason.trim().length < 3} onClick={go}>
            {t('res.cancel')}
          </Button>
        </>
      }
    >
      <Field label={t('res.why')}>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
      <div className="chips">
        {chips.map((c) => (
          <button key={c} className="chip" onClick={() => setReason(c)}>
            {c}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Customers */

interface Profile {
  customer: CustomerRow;
  stats: { visits: number; spent: number; average: number; last_visit: string | null };
  favourites: { product: string; name: string; qty: number }[];
  orders: { id: string; number: number; name: string; state: string; total: number; tip: number; guests: number; receipt_no: number | null; opened_at: string; closed_at: string | null }[];
  reservations?: { id: string; number: number; guests: number; state: State; date: string; time: string; table_name: string | null }[];
  reservation_counts?: { total: number; no_shows: number; upcoming: number };
  loyalty?: ProfileLoyalty;
}

function Customers() {
  const { can } = useSession();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'active' | 'vip' | 'archived'>('active');
  const [version, setVersion] = useState(0);
  const rows = useCustomerSearch(search, filter, version);
  const extra = useCustomColumns('customer.customer', rows?.map((c) => c.id) ?? []);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The search opens a customer (?open=<id>); quick create starts one (?new=customer).
  useLinkParam('open', setOpen);
  useLinkParam('new', (kind) => kind === 'customer' && can('customers.edit') && setAdding(true));
  return (
    <>
      <div className="toolbar">
        <div className="toolbar-group">
          <div className="search cust-search">
            <Icon name="search" size={18} />
            <input className="input" placeholder={t('cust.search')} aria-label={t('cust.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => setSearch('')} /> : null}
          </div>
          <div className="segmented" role="radiogroup" aria-label={t('res.show')}>
            {(['active', 'vip', 'archived'] as const).map((k) => (
              <button key={k} role="radio" aria-checked={filter === k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>
                {t(`cust.f_${k}`)}
              </button>
            ))}
          </div>
        </div>
        {can('customers.edit') ? (
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('cust.new')}
          </Button>
        ) : null}
      </div>
      <section className="list-card" aria-label={t('guests.tab_customers')}>
        {!rows ? (
          <Skeleton h={240} />
        ) : rows.length === 0 ? (
          <EmptyState icon="users" title={search ? t('cust.none_found', { q: search }) : t('cust.none_yet')} body={search ? undefined : t('cust.none_body')} />
        ) : (
          rows.map((c) => (
            <button key={c.id} className="list-row stock-row" onClick={() => setOpen(c.id)}>
              <span className="avatar" aria-hidden="true">
                {initials(c.name)}
              </span>
              <span className="list-main">
                <span className="strong">
                  {c.name} {c.vip ? <span className="vip">{t('cust.vip')}</span> : null}
                </span>
                <span className="muted">{[phoneText(c.phone), c.email].filter(Boolean).join(' · ') || t('cust.no_contact')}</span>
                {extra.line(c.id) ? <span className="muted small">{extra.line(c.id)}</span> : null}
                {c.allergies ? <AllergyNote text={c.allergies} compact /> : null}
              </span>
              <span className="row-end">
                <span className="strong">{money(c.spent)}</span>
                <span className="muted">
                  {tn('cust.visits', c.visits)}
                  {c.last_visit ? ` · ${localDay(c.last_visit)}` : ''}
                </span>
              </span>
            </button>
          ))
        )}
      </section>
      {open ? <CustomerDialog id={open} onClose={() => setOpen(null)} onChange={() => setVersion((v) => v + 1)} /> : null}
      {adding ? (
        <CustomerForm
          title={t('cust.new')}
          initial={/\d{3}/.test(search) ? { phone: search } : { name: search }}
          onClose={() => setAdding(false)}
          onSaved={(c) => {
            setAdding(false);
            setVersion((v) => v + 1);
            setOpen(c.id);
          }}
        />
      ) : null}
    </>
  );
}

function CustomerDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const navigate = useNavigate();
  const { can } = useSession();
  const { fail, toast } = useUi();
  const [p, setP] = useState<Profile | null>(null);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(0);
  const load = () => void action<Profile>('customers.get', { customer: id }).then(setP, fail);
  useEffect(load, [id]);
  if (editing && p) {
    return (
      <CustomerForm
        title={t('cust.edit', { name: p.customer.name })}
        initial={p.customer}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          setSaved((n) => n + 1);
          load();
          onChange();
        }}
      />
    );
  }
  const archive = async (active: boolean) => {
    try {
      await action('customers.archive', { customer: id, active });
      toast(active ? t('cust.restored', { name: p!.customer.name }) : t('cust.archived', { name: p!.customer.name }), 'info');
      onChange();
      onClose();
    } catch (err) {
      fail(err);
    }
  };
  return (
    <Dialog
      title={p?.customer.name ?? t('common.loading')}
      onClose={onClose}
      size="lg"
      footer={
        p ? (
          <>
            {can('customers.manage') ? (
              <Button variant="ghost" icon={p.customer.active ? 'archive' : 'undo'} onClick={() => void archive(!p.customer.active)}>
                {p.customer.active ? t('cust.archive') : t('cust.restore')}
              </Button>
            ) : null}
            {can('customers.edit') && p.customer.active ? (
              <Button variant="primary" icon="edit" onClick={() => setEditing(true)}>
                {t('cust.edit_details')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {!p ? (
        <Skeleton h={300} />
      ) : (
        <div className="profile">
          <div className="profile-head">
            <span className="avatar lg" aria-hidden="true">
              {initials(p.customer.name)}
            </span>
            <div className="pick-main">
              <span className="strong">
                {p.customer.name} {p.customer.vip ? <span className="vip">{t('cust.vip')}</span> : null}
                {!p.customer.active ? <StatusBadge tone="neutral" icon="archive" label={t('cust.archived_badge')} /> : null}
              </span>
              <span className="muted">{[phoneText(p.customer.phone), p.customer.email].filter(Boolean).join(' · ') || t('cust.no_contact')}</span>
              <span className="muted">
                {p.customer.birthday ? t('cust.born', { day: day(p.customer.birthday) }) : null}
                {p.customer.birthday && p.customer.marketing_at ? ' · ' : null}
                {p.customer.marketing_at ? t('cust.offers_ok') : null}
              </span>
            </div>
          </div>
          {p.customer.allergies ? <AllergyNote text={p.customer.allergies} /> : null}
          {p.customer.note ? <p className="booking-note">{p.customer.note}</p> : null}
          <CustomFieldsView target="customer.customer" record={id} version={saved} />
          <VisitStats visits={p.stats.visits} spent={p.stats.spent} average={p.stats.average} last={p.stats.last_visit} />
          {p.loyalty ? <CustomerLoyalty customer={id} data={p.loyalty} onChanged={load} /> : null}
          {p.reservation_counts && p.reservation_counts.total ? (
            <p className="muted">
              {tn('cust.bookings', p.reservation_counts.total)}
              {p.reservation_counts.upcoming ? ` · ${tn('cust.upcoming', p.reservation_counts.upcoming)}` : ''}
              {p.reservation_counts.no_shows ? (
                <span className="text-danger"> · {tn('res.past_no_shows', p.reservation_counts.no_shows)}</span>
              ) : null}
            </p>
          ) : null}
          <div className="profile-cols">
            <section>
              <h3>{t('cust.favourites')}</h3>
              {p.favourites.length ? (
                <ul className="rep-reasons">
                  {p.favourites.map((f) => (
                    <li key={f.product}>
                      <span>{f.name}</span>
                      <span className="muted">× {qty(f.qty)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('cust.no_orders')}</p>
              )}
            </section>
            <section>
              <h3>{t('cust.recent_bills')}</h3>
              {p.orders.length ? (
                <ul className="rep-reasons">
                  {p.orders.slice(0, 8).map((o) => (
                    <li key={o.id}>
                      {o.state === 'open' ? (
                        <button className="link" onClick={() => navigate(`/pos/order/${o.id}`)}>
                          {o.name} · {t('cust.open_now')}
                        </button>
                      ) : (
                        <span>
                          {dateTime(o.closed_at ?? o.opened_at)} · {o.name}
                        </span>
                      )}
                      <span className="strong">{money(o.total)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('cust.no_orders')}</p>
              )}
            </section>
          </div>
          <RecordNotes model="customer.customer" record={id} version={saved} />
          {p.reservations?.length ? (
            <section>
              <h3>{t('cust.reservations')}</h3>
              <ul className="rep-reasons">
                {p.reservations.slice(0, 6).map((r) => (
                  <li key={r.id}>
                    <span>
                      {day(r.date)}, {r.time} · {tn('cust.people', r.guests)}
                      {r.table_name ? ` · ${t('table.name', { name: r.table_name })}` : ''}
                    </span>
                    <StatusBadge tone={STATE_BADGE[r.state].tone} icon={STATE_BADGE[r.state].icon} label={t(STATE_BADGE[r.state].label)} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
